import { useCallback } from 'react';
import {
  CUSTOMER_PERMISSION_COVERED_IN_ROLE,
  CUSTOMER_PERMISSION_NAMES,
  CUSTOMER_ROLE_ADMIN_NAMES,
} from '@/constants/customer.constants';
import { CustomerPermissionOnly, CustomerRoleOnly } from '@/api/apis/customer';

type UsePermissionChangeHandlerArgs = {
  roleValues: CustomerRoleOnly[];
  permissionValues: CustomerPermissionOnly[];
  isPermissionMandatoryForActiveRole: (perm: CustomerPermissionOnly) => boolean;
  isPermissionSelectionForbiddenForRole: (perm: CustomerPermissionOnly) => boolean;
  updatePermissions: (next: CustomerPermissionOnly[]) => void;
  setPermissionRequiredModalData: (
    data: {
      scenario: 'cannotRemove' | 'cannotAdd';
      roleNames: string[];
      permissionName: string;
    } | null,
  ) => void;
};

export const usePermissionChangeHandler = ({
  roleValues,
  permissionValues,
  isPermissionMandatoryForActiveRole,
  isPermissionSelectionForbiddenForRole,
  updatePermissions,
  setPermissionRequiredModalData,
}: UsePermissionChangeHandlerArgs) => {
  return useCallback(
    (newPerms: string[]) => {
      const typed = newPerms as CustomerPermissionOnly[];

      const removed = permissionValues.find((p) => !typed.includes(p));
      const added = typed.find((p) => !permissionValues.includes(p));

      if (removed && isPermissionMandatoryForActiveRole(removed)) {
        const coveringRole = CUSTOMER_PERMISSION_COVERED_IN_ROLE[removed];
        const roleNames = coveringRole ? [CUSTOMER_ROLE_ADMIN_NAMES[coveringRole]].filter(Boolean) : [];
        setPermissionRequiredModalData({
          scenario: 'cannotRemove',
          roleNames,
          permissionName: CUSTOMER_PERMISSION_NAMES[removed] ?? '',
        });
        return;
      }

      if (added && isPermissionSelectionForbiddenForRole(added)) {
        setPermissionRequiredModalData({
          scenario: 'cannotAdd',
          roleNames: roleValues.map((r) => CUSTOMER_ROLE_ADMIN_NAMES[r]),
          permissionName: CUSTOMER_PERMISSION_NAMES[added] ?? '',
        });
        return;
      }

      updatePermissions(typed);
    },
    [
      roleValues,
      permissionValues,
      isPermissionMandatoryForActiveRole,
      isPermissionSelectionForbiddenForRole,
      updatePermissions,
      setPermissionRequiredModalData,
    ],
  );
};
